import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} StudentInfo
 * @property {string} name
 * @property {string} email
 * @property {string} username
 * @property {string} age
 * @property {string} student_code
 */

/**
 * @typedef {object} QuestInfo
 * @property {number} id
 * @property {string} title
 * @property {string | null} thumbnail
 * @property {string} type
 * @property {string | null} banner_img
 * @property {string | null} thumbnail_url
 * @property {string | null} banner_img_url
 */

/**
 * @typedef {object} EpisodeInfo
 * @property {string} episode
 * @property {string | number} best_score
 * @property {string | number} attempts
 */

/**
 * @typedef {object} StudentQuestReportResponse
 * @property {StudentInfo} student
 * @property {QuestInfo} quest
 * @property {EpisodeInfo[]} episodes
 */

/**
 * @typedef {object} GetStudentReportByQuestParams
 * @property {number | string} studentId - Student ID (required)
 * @property {number | string} questId - Quest ID (required)
 */

/**
 * Custom hook to fetch the student report by quest ID for teacher
 */
export const useGetStudentReportByQuestId = () => {
  const [report, setReport] = useState(
    /** @type {StudentQuestReportResponse | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch individual student report by student ID and quest ID
   * @async
   * @param {GetStudentReportByQuestParams} params
   */
  const getStudentReportByQuestId = async ({ studentId, questId } = {}) => {
    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/teacher/reports/students/${studentId}/${questId}`;

      const response = await axiosInstance.get(url);

      setReport(response?.data || null);
    } catch (err) {
      setReport(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    report,
    loading,
    error,
    getStudentReportByQuestId,
    setReport,
  };
};
